import { PDFNote } from '../models/PDFNote';
import { PDFNoteStatusVals } from '../../../helpers/mapping/PDFNoteStatusVals';
import { ProjectDoc } from '../../projectDocs/models/ProjectDoc';
import { ProjectDocFolderStatuses } from '../../../helpers/mapping/ProjectDocFolderStatuses';
import { ProjectDocStatusVals } from '../../../helpers/mapping/ProjectDocStatusVals';

/**
 * Что контрагент может сделать с замечанием.
 *
 * Контрагентский бэкенд (X5.PDA.CWebApi) не отдаёт Actions[] для замечаний,
 * поэтому права считаются на клиенте. Единственный источник истины —
 * этот файл: его используют и карточка замечания (NoteCardActions),
 * и сайдбар PDF-просмотрщика, чтобы наборы кнопок не разъехались.
 */
export interface ContractorNotePermissions {
  canComplete: boolean;
  canCancelCompletion: boolean;
}

export const NO_CONTRACTOR_PERMISSIONS: ContractorNotePermissions = {
  canComplete: false,
  canCancelCompletion: false,
};

/** Статус папки допускает действия контрагента. */
function folderAllowsActions(projectDocFolderStatusId: number): boolean {
  return (
    projectDocFolderStatusId === ProjectDocFolderStatuses.NoData ||
    projectDocFolderStatusId === ProjectDocFolderStatuses.OnContractorDevelopment ||
    projectDocFolderStatusId === ProjectDocFolderStatuses.Recalled
  );
}

/** Статус документа допускает действия контрагента. */
function docAllowsActions(projectDoc?: ProjectDoc): boolean {
  return projectDoc != null && projectDoc.StatusId !== ProjectDocStatusVals.Stopped;
}

/** Замечание уже считается закрытым — «Выполнить» больше не предлагаем. */
function noteShowsAsDone(note: PDFNote): boolean {
  return (
    note.StatusId === PDFNoteStatusVals.Completed ||
    note.StatusId === PDFNoteStatusVals.CompletionConfirmed ||
    note.StatusId === PDFNoteStatusVals.CanceledByGIP
  );
}

export function buildContractorNotePermissions(
  note: PDFNote,
  projectDocFolderStatusId: number,
  projectDoc?: ProjectDoc
): ContractorNotePermissions {
  const enabled = folderAllowsActions(projectDocFolderStatusId) && docAllowsActions(projectDoc);
  if (!enabled) return NO_CONTRACTOR_PERMISSIONS;

  return {
    canComplete: !noteShowsAsDone(note),
    canCancelCompletion: note.StatusId === PDFNoteStatusVals.Completed,
  };
}

/** Права по списку замечаний, ключ — Id замечания. Для PDF-просмотрщика. */
export function buildContractorNotePermissionsMap(
  notes: PDFNote[],
  projectDocFolderStatusId: number,
  projectDoc?: ProjectDoc
): Record<number, ContractorNotePermissions> {
  const map: Record<number, ContractorNotePermissions> = {};
  for (const note of notes) {
    if (note.Id == null) continue;
    map[note.Id] = buildContractorNotePermissions(note, projectDocFolderStatusId, projectDoc);
  }
  return map;
}
